/**
 * From CSV columns to dataset items. Each column goes into one of the item's
 * four slots — or is skipped:
 *
 *   input            what the agent gets: {column: value}, required
 *   expected_output  the answer you expect
 *   output           an answer an agent already gave (scored without a run)
 *   metadata         kept with the item, not sent to the agent
 *
 * With one column in Expected output or Output the slot is that cell itself;
 * with several it is an object {column: value}. A slot's own prefix is dropped
 * from field names (`input.report` → input.report, `expected.label` →
 * expected_output.label). Empty cells are left out, and so is a slot whose
 * columns are all empty on a row.
 *
 * `metadata.key` is the item's id across versions: a re-upload with the same key
 * updates the item (see `item-identity.ts`). It comes from the column marked
 * "Item key", else from a Metadata column whose field name is `key`.
 *
 * The first guess comes from the header; the person can change every column.
 * Pure functions, so the rules are tested without a DOM.
 */

import { itemIdentity } from "./item-identity";
import type { DatasetItemInput } from "./types";

export type Slot = "input" | "expected_output" | "output" | "metadata";
/** Where a column goes: a slot, the item key (in metadata), or nowhere. */
export type Target = Slot | "key" | "skip";

export interface ColumnMap {
  column: string;
  target: Target;
}

export interface Mapping {
  columns: ColumnMap[];
  /** Cells that are JSON objects or arrays are read as JSON, not text. */
  parseJson: boolean;
}

export const SLOTS: Slot[] = ["input", "expected_output", "output", "metadata"];

/** Prefixes dropped from a column name inside each slot. */
const PREFIXES: Record<Slot, string[]> = {
  input: ["input."],
  expected_output: ["expected_output.", "expected."],
  output: ["output."],
  metadata: ["metadata."],
};

export function guessTarget(column: string): Target {
  const name = column.trim();
  const is = (...names: string[]) => names.includes(name);
  const under = (...prefixes: string[]) => prefixes.some((p) => name.startsWith(p));
  if (is("key", "metadata.key")) return "key";
  if (is("expected_output", "expected") || under("expected.", "expected_output.")) return "expected_output";
  if (is("output") || under("output.")) return "output";
  if (is("id") || under("metadata.")) return "metadata";
  return "input";
}

export const autoMap = (header: string[]): Mapping => ({
  columns: header.map((column) => ({ column, target: guessTarget(column) })),
  parseJson: true,
});

/** The slot a target fills: the item key lives in metadata. */
export const slotOf = (target: Target): Slot | null =>
  target === "skip" ? null : target === "key" ? "metadata" : target;

/** The field name a column gets inside its slot. */
export function fieldName(column: string, target: Target): string {
  if (target === "key") return "key";
  const name = column.trim();
  const prefix = target === "skip" ? undefined : PREFIXES[target].find((p) => name.startsWith(p));
  return prefix && name.length > prefix.length ? name.slice(prefix.length) : name;
}

/** Index of the column that supplies `metadata.key`, if any. */
export function keyColumn(columns: ColumnMap[]): number | null {
  const marked = columns.findIndex((c) => c.target === "key");
  if (marked >= 0) return marked;
  const named = columns.findIndex(
    (c) => c.target === "metadata" && fieldName(c.column, "metadata") === "key",
  );
  return named >= 0 ? named : null;
}

/** Move one column; only one column can be the item key. */
export function assign(mapping: Mapping, index: number, target: Target): Mapping {
  const columns = mapping.columns.map((c, i): ColumnMap => {
    if (i === index) return { ...c, target };
    if (target === "key" && c.target === "key") return { ...c, target: "metadata" };
    return c;
  });
  return { ...mapping, columns };
}

/** A JSON object or array when the cell holds one (and parsing is on); the text otherwise. */
export function parseCell(text: string, parseJson = true): unknown {
  if (!parseJson || !/^[[{]/.test(text)) return text;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text; // not JSON after all — keep the text
  }
}

export function buildItem(row: string[], mapping: Mapping): DatasetItemInput {
  const fields: Record<Slot, Record<string, unknown>> = {
    input: {},
    expected_output: {},
    output: {},
    metadata: {},
  };
  const assigned: Record<Slot, number> = { input: 0, expected_output: 0, output: 0, metadata: 0 };
  const key = keyColumn(mapping.columns);
  mapping.columns.forEach((c, i) => {
    const slot = slotOf(c.target);
    if (!slot) return;
    assigned[slot] += 1;
    const raw = (row[i] ?? "").trim();
    if (raw && i !== key) fields[slot][fieldName(c.column, c.target)] = parseCell(raw, mapping.parseJson);
  });
  const keyCell = key === null ? "" : (row[key] ?? "").trim();
  if (keyCell) fields.metadata.key = keyCell; // set last: the marked key wins over a `key` field

  /** One column: the cell itself. Several: an object. All empty: left out. */
  const answer = (slot: Slot) => {
    const values = Object.values(fields[slot]);
    if (values.length === 0) return {};
    return { [slot]: assigned[slot] === 1 ? values[0] : fields[slot] };
  };
  return {
    input: fields.input,
    ...answer("expected_output"),
    ...answer("output"),
    ...(Object.keys(fields.metadata).length ? { metadata: fields.metadata } : {}),
  };
}

export interface RowCheck {
  /** 0-based data row; the file line is `index + 2` (after the header). */
  index: number;
  item: DatasetItemInput;
  noInput: boolean;
  noExpected: boolean;
  /** File line of the earlier row with the same identity (see `itemIdentity`), if any. */
  repeatOf: number | null;
}

export function checkRows(rows: string[][], mapping: Mapping): RowCheck[] {
  const first = new Map<string, number>();
  return rows.map((row, index) => {
    const item = buildItem(row, mapping);
    const noInput = Object.keys(item.input).length === 0;
    const identity = itemIdentity(item);
    const earlier = noInput ? undefined : first.get(identity);
    if (!noInput && earlier === undefined) first.set(identity, index + 2);
    return {
      index,
      item,
      noInput,
      noExpected: item.expected_output === undefined,
      repeatOf: earlier ?? null,
    };
  });
}

export interface ImportPlan {
  items: DatasetItemInput[];
  skippedNoInput: number;
  /** Rows that repeat an earlier row of the file; only the first is kept. */
  skippedRepeats: number;
  /** Kept rows with no expected output. Only a note: evaluators that compare with one skip them. */
  noExpected: number;
}

export function planImport(checks: RowCheck[]): ImportPlan {
  const kept = checks.filter((c) => !c.noInput && c.repeatOf === null);
  return {
    items: kept.map((c) => c.item),
    skippedNoInput: checks.filter((c) => c.noInput).length,
    skippedRepeats: checks.filter((c) => !c.noInput && c.repeatOf !== null).length,
    noExpected: kept.filter((c) => c.noExpected).length,
  };
}
